import { ArrowBack, ArrowForward, Close, Download, ErrorOutline, Warning } from '@mui/icons-material';
import { Box, ButtonBase, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { snackCaller } from '../App';
import util from '../util';
import Image from './Image';

function FileViewer(props) {
    let {
        fileModalData,
        setFileModalData,
        fileDatas,
        setFileModalVisible,
        onClose
    } = props

    const fileModalDataRef = useRef(fileModalData)
    const hideNameTimerRef = useRef(null)
    const forceShowNameUntilRef = useRef(0)

    const IMAGE_PREVIEW_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "jfif", "avif", "heic", "heif", "apng"];
    const VIDEO_PREVIEW_EXTENSIONS = ["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv", "m4v", "ogv"];
    const AUDIO_PREVIEW_EXTENSIONS = ["mp3", "wav", "ogg", "oga", "m4a", "aac", "flac", "opus", "weba"];

    const getExtension = (filename = "") => {
        const originalName = filename.split("$Ğ!Ğ$")[0] || "";
        return originalName.split('.').pop()?.toLowerCase() || "";
    }

    const isPreviewableItem = (item = {}) => {
        const extension = getExtension(item.filename);
        const mimetype = (item.mimetype || "").toLowerCase();

        return IMAGE_PREVIEW_EXTENSIONS.includes(extension)
            || VIDEO_PREVIEW_EXTENSIONS.includes(extension)
            || AUDIO_PREVIEW_EXTENSIONS.includes(extension)
            || mimetype.startsWith("image/")
            || mimetype.startsWith("video/")
            || mimetype.startsWith("audio/")
            || mimetype.includes("jfif");
    }

    const isVideoItem = (item = {}) => {
        const extension = getExtension(item.filename);
        const mimetype = (item.mimetype || "").toLowerCase();

        return VIDEO_PREVIEW_EXTENSIONS.includes(extension) || mimetype.startsWith("video/");
    }

    const isAudioItem = (item = {}) => {
        const extension = getExtension(item.filename);
        const mimetype = (item.mimetype || "").toLowerCase();

        return AUDIO_PREVIEW_EXTENSIONS.includes(extension) || mimetype.startsWith("audio/");
    }

    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const [translateY, setTranslateY] = useState(0);
    const [showFileName, setShowFileName] = useState(true);
    const [fileNamePinned, setFileNamePinned] = useState(false);

    const TOP_CLICK_HEIGHT = 120;
    const RIBBON_PROXIMITY_HEIGHT = 96;
    const RIBBON_PROXIMITY_WIDTH = 360;
    const FILE_NAME_CHANGE_GRACE_MS = 900;

    const clearHideNameTimer = () => {
        if (hideNameTimerRef.current) {
            clearTimeout(hideNameTimerRef.current);
            hideNameTimerRef.current = null;
        }
    }

    const scheduleHideNameRibbon = () => {
        clearHideNameTimer();
        hideNameTimerRef.current = setTimeout(() => {
            setShowFileName(false);
            setFileNamePinned(false);
        }, 3000);
    }

    const handleBackButt = (FMD) => {
        // get the previous file or wraparound
        let currentIndex = fileDatas.indexOf(fileDatas.find(f => f._id === FMD._id))
        let newFileIndex = currentIndex - 1 < 0 ? fileDatas.length - 1 : currentIndex - 1
        let item = fileDatas[newFileIndex]

        let guard = 0
        while (item && !isPreviewableItem(item) && guard < fileDatas.length) {
            if (fileDatas[newFileIndex - 1]) {
                item = fileDatas[newFileIndex - 1]
                newFileIndex--
            } else {
                item = fileDatas[fileDatas.length - 1]
                newFileIndex = fileDatas.length - 1
            }
            guard++
        }

        if (!item || !isPreviewableItem(item)) return;

        setFileModalData(item)
        setZoomLevel(1);
        setTranslateX(0);
        setTranslateY(0);
    }

    const handleForwardButt = (FMD) => {
        // get the next file or wraparound
        let currentIndex = fileDatas.indexOf(fileDatas.find(f => f._id === FMD._id))
        let newFileIndex = currentIndex + 1 < fileDatas.length - 1 ? currentIndex + 1 : 0
        let item = fileDatas[newFileIndex]

        let guard = 0
        while (item && !isPreviewableItem(item) && guard < fileDatas.length) {
            if (fileDatas[newFileIndex + 1]) {
                item = fileDatas[newFileIndex + 1]
                newFileIndex++
            } else {
                item = fileDatas[0]
                newFileIndex = 0
            }
            guard++
        }

        if (!item || !isPreviewableItem(item)) return;

        setFileModalData(item)
        setZoomLevel(1);
        setTranslateX(0);
        setTranslateY(0);
    }

    const closeViewer = () => {
        if (onClose) {
            onClose()
            return
        }
        setFileModalVisible(false)
        setFileModalData({})
    }

    const handleDownload = (item) => {
        const anchor = document.createElement("a");
        anchor.href = item.url;
        anchor.download = item.filename?.split("$Ğ!Ğ$")[0] || "download";
        anchor.target = "_blank";
        anchor.rel = "noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    const handleWheelZoom = (event) => {
        const delta = Math.sign(event.deltaY);
        let newZoomLevel = zoomLevel - delta * 0.1;
        newZoomLevel = Math.max(1, Math.min(5, newZoomLevel)); // Clamp zoom between 1 and 5
        setZoomLevel(newZoomLevel);
    };

    const handleDoubleClickZoom = () => {
        if (zoomLevel === 1) {
            setZoomLevel(2);
        } else {
            setZoomLevel(1);
            setTranslateX(0);
            setTranslateY(0);
        }
    };

    const handleMouseDown = (event) => {
        if (zoomLevel > 1) {
            setIsDragging(true);
            setStartX(event.clientX - translateX);
            setStartY(event.clientY - translateY);
        }
    };

    const handleMouseMove = (event) => {
        if (isDragging) {
            setTranslateX(event.clientX - startX);
            setTranslateY(event.clientY - startY);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        let arrowListener = (ev) => {
            try {
                if (ev.key === "ArrowLeft") { // GO BACK
                    document.getElementById("bbfm").click()
                } else if (ev.key === "ArrowRight") { // GO FORWARD
                    document.getElementById("fbfm").click()
                }
            } catch (e) {

            }
        }

        window.addEventListener("keydown", arrowListener)

        return () => {
            window.removeEventListener("keydown", arrowListener)
        }
    }, [])

    useEffect(() => {
        if (!fileModalData?._id) return;

        forceShowNameUntilRef.current = Date.now() + FILE_NAME_CHANGE_GRACE_MS;
        setShowFileName(true);
        setFileNamePinned(false);
        scheduleHideNameRibbon();

        return () => {
            clearHideNameTimer();
        }
    }, [fileModalData?._id])

    useEffect(() => {
        return () => {
            clearHideNameTimer();
        }
    }, [])

    const handleViewerMouseMove = (event) => {
        if (Date.now() < forceShowNameUntilRef.current) {
            setShowFileName(true);
            return;
        }

        const centerX = window.innerWidth / 2;
        const nearNameRibbon = event.clientY <= RIBBON_PROXIMITY_HEIGHT
            && Math.abs(event.clientX - centerX) <= RIBBON_PROXIMITY_WIDTH;

        if (nearNameRibbon) {
            setShowFileName(true);
            clearHideNameTimer();
            return;
        }

        if (fileNamePinned && event.clientY > TOP_CLICK_HEIGHT) {
            setFileNamePinned(false);
            setShowFileName(false);
            return;
        }

        if (!fileNamePinned) {
            setShowFileName(false);
        }
    }

    const handleViewerClickCapture = (event) => {
        if (Date.now() < forceShowNameUntilRef.current && event.clientY > TOP_CLICK_HEIGHT) {
            return;
        }

        if (event.clientY <= TOP_CLICK_HEIGHT) {
            setFileNamePinned(true);
            setShowFileName(true);
            clearHideNameTimer();
            return;
        }

        setFileNamePinned(false);
        setShowFileName(false);
    }

    if (!fileModalData._id) {
        return <></> // return prematurely cause no data lol
    }

    if (!fileDatas) {
        return <></> // return prematurely cause no file datas
    }

    let file = fileDatas.find(f => f._id === fileModalData._id)

    if (!file) {
        return <></> // return prematurely cause can't find file
    }

    // find the adjacent files, one before and one after the current file
    let currentIndex = fileDatas.indexOf(file)
    let prevFile = fileDatas[currentIndex - 1] || fileDatas[fileDatas.length - 1]
    let nextFile = fileDatas[currentIndex + 1] || fileDatas[0]
    const currentIsVideo = isVideoItem(fileModalData)
    const currentIsAudio = isAudioItem(fileModalData)
    const disableZoomAndDrag = currentIsVideo || currentIsAudio

    return (
        <Box
            sx={{
                width: "100vw",
                height: "100vh",
                textAlign: "center",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#000"
            }}
            onMouseMove={handleViewerMouseMove}
            onClickCapture={handleViewerClickCapture}
        >
            {
                <div
                    style={{
                        display: "none"
                    }}
                >
                    {prevFile && !isVideoItem(prevFile) && !isAudioItem(prevFile) && <Image
                        src={prevFile.url}
                    />}

                    {nextFile && !isVideoItem(nextFile) && !isAudioItem(nextFile) && <Image
                        src={nextFile.url}
                    />}
                </div>
            }
            <Typography sx={{
                position: "absolute",
                zIndex: 3,
                backdropFilter: "blur(5px) brightness(0.7)",
                alignSelf: "center",
                fontSize: "1vw",
                opacity: showFileName ? 1 : 0,
                transition: "opacity 180ms ease",
                pointerEvents: "none",
            }} variant="h5">
                {file.filename.split("$Ğ!Ğ$")[0]}
            </Typography>

            <Box
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 5
                }}
            >
                <IconButton
                    onClick={() => {
                        handleDownload(fileModalData);
                    }}
                >
                    <Download />
                </IconButton>

                <IconButton onClick={closeViewer}>
                    <Close />
                </IconButton>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    zIndex: 2,
                    pointerEvents: "none"
                }}
            >
                <ButtonBase
                    id="bbfm"
                    sx={{
                        position: "absolute",
                        width: "65px",
                        height: "100%",
                        left: 0,
                        pointerEvents: "all"
                    }}

                    onClick={() => handleBackButt(fileModalData)}
                >
                    <ArrowBack />
                </ButtonBase>

                <ButtonBase
                    id="fbfm"
                    sx={{
                        position: "absolute",
                        width: "65px",
                        height: "100%",
                        right: 0,
                        pointerEvents: "all"
                    }}
                    onClick={() => handleForwardButt(fileModalData)}
                >
                    <ArrowForward />
                </ButtonBase>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    zIndex: 1,
                    top: 0,
                    display: "flex",
                    justifyContent: "center",
                    overflow: "hidden" // Add overflow hidden to prevent image from spilling out
                }}
                onWheel={disableZoomAndDrag ? undefined : handleWheelZoom}
                onMouseDown={disableZoomAndDrag ? undefined : handleMouseDown}
                onMouseMove={disableZoomAndDrag ? undefined : handleMouseMove}
                onMouseUp={disableZoomAndDrag ? undefined : handleMouseUp}
                onMouseLeave={disableZoomAndDrag ? undefined : handleMouseUp}
            >
                {currentIsVideo ? (
                    <video
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            backgroundColor: "#000"
                        }}
                        src={fileModalData.url}
                        controls
                        autoPlay
                        playsInline
                        loop
                    />
                ) : currentIsAudio ? (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            gap: 2,
                            px: 4
                        }}
                    >
                        <Typography sx={{ opacity: 0.9 }}>
                            {file.filename.split("$Ğ!Ğ$")[0]}
                        </Typography>
                        <audio
                            style={{ width: "min(800px, 90%)" }}
                            src={fileModalData.url}
                            controls
                            autoPlay
                            loop
                        />
                    </Box>
                ) : (
                    <Image
                        style={{
                            width: `${zoomLevel * 100}%`,
                            height: `${zoomLevel * 100}%`,
                            objectFit: "contain",
                            transformOrigin: "center",
                            transition: "transform 50ms ease", // Add smooth transition for zoom
                            transform: `translate(${translateX}px, ${translateY}px)`, // Add translate transform
                        }}
                        onDragStart={(ev) => ev.preventDefault()}
                        src={fileModalData.url}
                        item={fileModalData}
                        onDoubleClick={handleDoubleClickZoom} // Add double click event listener
                    />
                )}
            </Box>
        </Box>
    )
}


export default FileViewer;